// src/pages/Login.tsx
// Styled to match app dashboard

import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getSupabase } from "@/lib/supabase";

function OTPInput({ value, onChange, onComplete }: { 
  value: string; 
  onChange: (val: string) => void;
  onComplete: () => void;
}) {
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  const handleInput = (index: number, inputValue: string) => {
    const digit = inputValue.replace(/\D/g, "").slice(-1);
    if (digit) {
      const newCode = value.slice(0, index) + digit + value.slice(index + 1);
      onChange(newCode.slice(0, 6));
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
      if (newCode.length === 6) {
        setTimeout(onComplete, 100);
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[index]) {
        onChange(value.slice(0, index) + value.slice(index + 1));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        onChange(value.slice(0, index - 1) + value.slice(index));
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (paste) {
      onChange(paste);
      inputRefs.current[Math.min(paste.length, 5)]?.focus();
      if (paste.length === 6) {
        setTimeout(onComplete, 100);
      }
    }
  };

  return (
    <div className="flex justify-center gap-3" onPaste={handlePaste}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] ?? ""}
          onChange={(e) => handleInput(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onFocus={(e) => e.target.select()}
          className={`
            w-11 h-13 bg-[#f1f5f9] border-2 rounded-xl
            text-xl font-semibold text-[#0f172a] text-center
            focus:outline-none transition-all duration-200
            ${value[i] 
              ? "border-[#a78bfa] bg-[#a78bfa]/5" 
              : "border-[#e2e8f0] focus:border-[#a78bfa]/50"
            }
          `}
        />
      ))}
    </div>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"email" | "code">("email");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  
  const navigate = useNavigate();
  const supabase = getSupabase();

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({
  email: email.toLowerCase().trim(),
  options: { shouldCreateUser: false },
});
      if (error) throw error;
      setStep("code");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
  setError(null);
  setLoading(true);
  
  const { data, error } = await supabase.auth.verifyOtp({
    email: email.toLowerCase().trim(),
    token: otp,
    type: 'email',
  });
  
  if (error) {
    setError(error.message);
    setLoading(false);
    return;
  }
  
  navigate("/dashboard", { replace: true });
};

  return (
    <div className="min-h-screen bg-[#ffffff] text-[#0f172a]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm">Back</span>
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-[#a78bfa] flex items-center justify-center">
            <svg className="w-5 h-5 text-[#0f172a]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="font-semibold text-[#0f172a]">Input</span>
        </div>
        <div className="w-16"></div> {/* Spacer to balance back button */}
      </header>

      {/* Main Content */}
      <main className="flex flex-col items-center justify-center px-4 py-8 min-h-[calc(100vh-140px)]">
        <div className="w-full max-w-md">
          
          {step === "email" ? (
            <>
              {/* Title */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
                  Welcome back
                </h1>
                <p className="text-gray-500 text-sm">
                  Log in to your account
                </p>
              </div>

              {/* Form Card */}
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6">
                <form onSubmit={handleSendOtp}>
                  {/* Email Input */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-500 mb-2">
                      Email address
                    </label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full h-12 px-4 bg-[#f1f5f9] border border-[#e2e8f0] rounded-xl text-[#0f172a] text-base placeholder-gray-600 focus:outline-none focus:border-[#a78bfa]/50 focus:ring-1 focus:ring-[#a78bfa]/20 transition-all"
                      autoFocus
                      required
                    />
                  </div>

                  {/* Continue Button */}
                  <button
                    type="submit"
                    disabled={loading || !email}
                    className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Sending...
                      </>
                    ) : "Send code"}
                  </button>
                </form>

                {/* Error */}
                {error && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <p className="text-red-400 text-sm text-center">{error}</p>
                  </div>
                )}
              </div>

              {/* Signup Link */}
              <div className="text-center mt-6">
                <p className="text-gray-500 text-sm">
                  Don't have an account?{" "}
                  <Link to="/signup" className="text-[#a78bfa] hover:underline font-medium">
                    Sign up
                  </Link>
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Back Button */}
              <button
                onClick={() => { setStep("email"); setOtp(""); setError(null); }}
                className="flex items-center gap-1.5 text-gray-500 hover:text-[#0f172a] transition-colors mb-6"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              {/* Title */}
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-[#0f172a] mb-2">
                  Check your email
                </h1>
                <p className="text-gray-500 text-sm">
                  We sent a 6-digit code to{" "}
                  <span className="text-[#a78bfa]">{email}</span>
                </p>
              </div>

              {/* OTP Card */}
              <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-6">
                <div className="mb-6">
                  <OTPInput 
                    value={otp} 
                    onChange={setOtp} 
                    onComplete={handleVerifyOtp}
                  />
                </div>

                {/* Verify Button */}
                <button
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                  className="w-full h-12 bg-[#a78bfa] hover:bg-[#a78bfa]/90 disabled:bg-[#f1f5f9] disabled:text-gray-600 text-[#0f172a] font-semibold text-base rounded-xl transition-all duration-200 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Verifying...
                    </>
                  ) : "Verify & Log in"}
                </button>

                {/* Error */}
                {error && (
                  <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <p className="text-red-400 text-sm text-center">{error}</p>
                  </div>
                )}

                {/* Resend */}
                <div className="text-center mt-4">
                  <button
                    onClick={() => { setStep("email"); setOtp(""); }}
                    className="text-gray-500 hover:text-gray-600 text-sm transition-colors"
                  >
                    Didn't receive it? Try again
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center border-t border-[#e2e8f0]">
        <p className="text-gray-600 text-xs">
          © {new Date().getFullYear()} Input
        </p>
      </footer>
    </div>
  );
}
